type Range = { min: number; max: number };

/**
 * Where a figure lands against its style's range (Tyler, 2026-10-02): the range is the green middle half
 * of the bar, yellow just outside it turning orange and then red at the ends, with a mark at the figure.
 * Half the range's width shows either side, as the recipe's page always has; a figure further off sits at
 * the end. Without a range the bar is an empty track, so the figures beside it still line up.
 */
export function StyleBar({
  range,
  value,
  title,
}: {
  range: Range | null | undefined;
  value: number | null | undefined;
  title?: string;
}) {
  // Not "empty": that is the page-wide empty state, whose padding blew the track up into a gray box.
  if (!range) return <div className="style-bar no-range" title={title} aria-hidden />;
  return (
    <div
      className="style-bar"
      title={title}
      role="img"
      aria-label={
        value == null
          ? `The style's ${range.min} to ${range.max}`
          : `${inRange(range, value) ? "Within" : "Outside"} the style's ${range.min} to ${range.max}`
      }
    >
      {value == null ? null : (
        <span className="style-bar-mark" style={{ left: `${barPosition(range, value)}%` }} />
      )}
    </div>
  );
}

export const inRange = (range: Range, value: number) => value >= range.min && value <= range.max;

/** How far along the bar a figure sits, 0 to 100, with the range from 25 to 75. */
export function barPosition(range: Range, value: number) {
  const span = Math.max(range.max - range.min, 1e-9);
  const lo = range.min - span / 2;
  const hi = range.max + span / 2;
  return Math.min(100, Math.max(0, ((value - lo) / (hi - lo)) * 100));
}
